import React from "react";
import { Link, useParams } from "react-router-dom";
import useFetch from "../../useFetch";
import EmptyState from "../Common/EmptyState";
import { getSafeExternalUrl } from "../../utils/externalUrls";
import "../Candidates/Candidates.css";

const initials = (name) => String(name || "C").split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word.charAt(0)).join("").toUpperCase();

const PublicCompanyDetails = () => {
  const { id } = useParams();
  const { response, isLoading, error } = useFetch(process.env.REACT_APP_BASE_API_URL + `/company-profiles/${id}`);
  const company = response?.company_profile;
  const safeWebsite = getSafeExternalUrl(company?.website);

  return (
    <>
      <section className="page-title">
        <div className="auto-container">
          <div className="title-outer">
            <h1>Company Profile</h1>
            <ul className="page-breadcrumb">
              <li><Link to="/">Home</Link></li>
              <li><Link to="/companies">Companies</Link></li>
              <li>Profile</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="ls-section public-candidate-detail">
        <div className="auto-container">
          {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
          {isLoading && <h5 className="text-center my-4">Loading Data</h5>}
          {!error && !isLoading && !company && (
            <EmptyState
              icon="la la-building"
              title="Company not found"
              text="This company profile may have been removed or made unavailable."
              actionText="Browse Companies"
              actionTo="/companies"
            />
          )}
          {!error && !isLoading && company && (
            <div className="row">
              <div className="col-lg-4">
                <div className="ls-widget candidate-profile-card">
                  <div className="widget-content">
                    <div className="candidate-profile-avatar">
                      {company.logo_url ? <img src={company.logo_url} alt={company.company_name} /> : <span>{initials(company.company_name)}</span>}
                    </div>
                    <h4>{company.company_name}</h4>
                    <p>{company.industry || "Company"}</p>
                    <ul>
                      <li><span className="la la-map-marker"></span>{company.location || "Location not added"}</li>
                      <li><span className="la la-briefcase"></span>{company.open_jobs_count ?? 0} open jobs</li>
                      <li><span className="la la-users"></span>{company.company_size || "Company size not added"}</li>
                      <li><span className="la la-calendar"></span>{company.founded_year ? `Founded ${company.founded_year}` : "Founded year not added"}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-lg-8">
                <div className="ls-widget candidate-profile-summary">
                  <div className="widget-title">
                    <h4>Company Summary</h4>
                    <Link to="/companies" className="theme-btn btn-style-three">Back</Link>
                  </div>
                  <div className="widget-content">
                    <h5>About</h5>
                    <p>{company.overview || "This company has not added an overview yet."}</p>

                    {company.culture && (
                      <>
                        <h5>Culture</h5>
                        <p>{company.culture}</p>
                      </>
                    )}

                    {(safeWebsite || company.email || company.phone) && (
                      <>
                        <h5>Company Links</h5>
                        <div className="candidate-profile-links">
                          {safeWebsite && <a href={safeWebsite} target="_blank" rel="noreferrer">Website</a>}
                          {company.email && <a href={`mailto:${company.email}`}>Email</a>}
                          {company.phone && <a href={`tel:${company.phone}`}>Call</a>}
                        </div>
                      </>
                    )}

                    {company.jobs?.length > 0 && (
                      <>
                        <h5>Open Jobs</h5>
                        <div className="company-open-jobs">
                          {company.jobs.map((job) => (
                            <Link to={`/jobs/${job.id}`} className="company-open-job" key={job.id}>
                              <strong>{job.title}</strong>
                              <span>{[job.location, job.job_type, job.workplace_type].filter(Boolean).join(" | ")}</span>
                            </Link>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default PublicCompanyDetails;
